import React from 'react';
import { useMutation, useQuery } from 'react-query';
import { API } from 'app/utils/constants';
import {
    CustomerImpactDto,
    CustomerImpactEditDto,
    CustomerImpactEditResultDto,
    CustomerImpactEditSnapshotDto,
    CxpApiClient,
} from 'generated/cxp-backend';
import {
    CustomerImpactDto as SdsgCustomerImpactDto,
    CustomerImpactEditDto as SdsgCustomerImpactEditDto,
    SdsgApiClient,
} from 'generated/sdsg-backend';
import {
    CrewApiClient,
    CustomerImpactDto as CrewCustomerImpactDto,
    CustomerImpactEditDto as CrewCustomerImpactEditDto,
} from 'generated/crew-backend';
import {
    CssApiClient,
    CustomerImpactDto as CssCustomerImpactDto,
    CustomerImpactEditDto as CssCustomerImpactEditDto,
} from 'generated/css-backend';
import { IRequestDto, RequestApiClient, RequestDomain, RequestType } from 'generated/request-backend';

const baseUrl = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0, -1) : API.API_SERVER_URL;

const http = (signal?: AbortSignal) => ({
    fetch: (url: RequestInfo, options?: RequestInit) =>
        window.fetch(url, signal ? { ...options, signal } : options),
});

const requestId = (request: IRequestDto) => {
    if (!request.unifiedTrackingId) throw new Error('The owning ticket is unavailable.');
    return request.unifiedTrackingId;
};

const unsupportedOwner = (): never => {
    throw new Error('The owning ticket is unavailable.');
};

const isCxpType = (type?: RequestType) => type != null && [
    RequestType.CXP_VariantRequest,
    RequestType.CXP_PFR_Request,
    RequestType.CXP_MessagingRequest,
    RequestType.CXP_TechnicalRequest,
    RequestType.CXP_ProgramRequest,
].includes(type);

const snapshot = async (
    request: IRequestDto,
    selector: CustomerImpactDto,
    signal?: AbortSignal,
): Promise<CustomerImpactEditSnapshotDto> => {
    const id = requestId(request);
    let value;
    switch (request.requestTeam) {
        case RequestDomain.SDSG: {
            const client = new SdsgApiClient(baseUrl, http(signal));
            const input = SdsgCustomerImpactDto.fromJS(selector.toJSON());
            value = request.requestType === RequestType.Proposal
                ? await client.proposal_CustomerImpactEditSnapshot(id, input)
                : request.requestType === RequestType.Question
                    ? await client.question_CustomerImpactEditSnapshot(id, input)
                    : request.requestType === RequestType.Rfx
                        ? await client.rfx_CustomerImpactEditSnapshot(id, input)
                        : unsupportedOwner();
            break;
        }
        case RequestDomain.CXP:
            value = isCxpType(request.requestType)
                ? await new CxpApiClient(baseUrl, http(signal))
                    .irtRequest_CustomerImpactEditSnapshot(id, selector)
                : unsupportedOwner();
            break;
        case RequestDomain.CSS: {
            const client = new CssApiClient(baseUrl, http(signal));
            const input = CssCustomerImpactDto.fromJS(selector.toJSON());
            value = request.requestType === RequestType.CSSRequest
                ? await client.cssRequest_CustomerImpactEditSnapshot(id, input)
                : request.requestType === RequestType.CssReviewRequest
                    ? await client.reviewRequest_CustomerImpactEditSnapshot(id, input)
                    : unsupportedOwner();
            break;
        }
        case RequestDomain.Crew: {
            const client = new CrewApiClient(baseUrl, http(signal));
            const input = CrewCustomerImpactDto.fromJS(selector.toJSON());
            value = request.requestType === RequestType.PreSales
                ? await client.preSales_CustomerImpactEditSnapshot(id, input)
                : [
                    RequestType.PostSales,
                    RequestType.CREW_WarrantyRequest,
                    RequestType.CREW_ProductRequest,
                    RequestType.CREW_MessagingRequest,
                ].includes(request.requestType)
                    ? await client.postSales_CustomerImpactEditSnapshot(id, input)
                    : unsupportedOwner();
            break;
        }
        default:
            throw new Error('The owning ticket is unavailable.');
    }
    const result = CustomerImpactEditSnapshotDto.fromJS(value?.toJSON());
    if (!result.version?.trim() || !result.originalFingerprint?.trim() || !result.impact) {
        throw new Error('The authoritative customer snapshot could not be confirmed.');
    }
    return result;
};

const save = async (
    request: IRequestDto,
    edit: CustomerImpactEditDto,
): Promise<CustomerImpactEditResultDto> => {
    const id = requestId(request);
    let value;
    switch (request.requestTeam) {
        case RequestDomain.SDSG: {
            const client = new SdsgApiClient(baseUrl);
            const input = SdsgCustomerImpactEditDto.fromJS(edit.toJSON());
            value = request.requestType === RequestType.Proposal
                ? await client.proposal_EditCustomerImpact(id, input)
                : request.requestType === RequestType.Question
                    ? await client.question_EditCustomerImpact(id, input)
                    : request.requestType === RequestType.Rfx
                        ? await client.rfx_EditCustomerImpact(id, input)
                        : unsupportedOwner();
            break;
        }
        case RequestDomain.CXP:
            value = isCxpType(request.requestType)
                ? await new CxpApiClient(baseUrl).irtRequest_EditCustomerImpact(id, edit)
                : unsupportedOwner();
            break;
        case RequestDomain.CSS: {
            const client = new CssApiClient(baseUrl);
            const input = CssCustomerImpactEditDto.fromJS(edit.toJSON());
            value = request.requestType === RequestType.CSSRequest
                ? await client.cssRequest_EditCustomerImpact(id, input)
                : request.requestType === RequestType.CssReviewRequest
                    ? await client.reviewRequest_EditCustomerImpact(id, input)
                    : unsupportedOwner();
            break;
        }
        case RequestDomain.Crew: {
            const client = new CrewApiClient(baseUrl);
            const input = CrewCustomerImpactEditDto.fromJS(edit.toJSON());
            value = request.requestType === RequestType.PreSales
                ? await client.preSales_EditCustomerImpact(id, input)
                : [
                    RequestType.PostSales,
                    RequestType.CREW_WarrantyRequest,
                    RequestType.CREW_ProductRequest,
                    RequestType.CREW_MessagingRequest,
                ].includes(request.requestType)
                    ? await client.postSales_EditCustomerImpact(id, input)
                    : unsupportedOwner();
            break;
        }
        default:
            throw new Error('The owning ticket is unavailable.');
    }
    const result = CustomerImpactEditResultDto.fromJS(value?.toJSON());
    if (typeof result.persisted !== 'boolean' || typeof result.synchronized !== 'boolean'
        || (result.persisted && !result.editId)) {
        throw new Error('The customer edit outcome could not be confirmed.');
    }
    return result;
};

const reconcile = async (request: IRequestDto) => {
    const completed = await new RequestApiClient(baseUrl)
        .request_ReconcileCustomerImpactEdits(requestId(request));
    if (completed !== true) throw new Error('Customer edit synchronization could not be confirmed.');
};

export const useCustomerImpactEditSnapshot = (
    request: IRequestDto,
    selector: CustomerImpactDto | undefined,
) => {
    const controller = React.useRef<AbortController>();
    React.useEffect(() => () => controller.current?.abort(), []);
    return useQuery(
        ['customerImpactEditSnapshot', request.unifiedTrackingId, selector?.toJSON()],
        async () => {
            controller.current?.abort();
            controller.current = new AbortController();
            return snapshot(request, selector!, controller.current.signal);
        },
        {
            enabled: !!selector && !!request.unifiedTrackingId,
            cacheTime: 0,
            retry: false,
        },
    );
};

export const useSaveCustomerImpactEdit = () =>
    useMutation(({ request, edit }: { request: IRequestDto; edit: CustomerImpactEditDto }) =>
        save(request, edit));

export const useReconcileCustomerImpactEdit = () =>
    useMutation((request: IRequestDto) => reconcile(request));
